import { Button, Group, Select } from "@mantine/core";
import type { DateTime } from "luxon";
import { useState } from "react";
import {
  type DateInputOnChangeValue,
  DateRangeInput
} from "../../../mantine/dates/DateRangeInput/DateRangeInput";
import { DateRangePresets } from "../../../mantine/dates/DateRangeInput/DateRangeInput.constants";
import { useToast } from "../../../mantine/notifications/useToast";
import type { SiteBasic } from "../../../utils/backend-types";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { NoDataMessage } from "../../BuildingBlocks/NoDataMessage/NoDataMessage";
import { LoadOrError } from "../../LoadOrError/LoadOrError";
import type { GuaranteeOfOriginPeriod } from "../GuaranteeOfOrigin.types";
import { useGOCheck } from "../hooks/useGOCheck";
import { useGOGenerate } from "../hooks/useGOGenerate";
import "./GuaranteeOfGenerationView.scss";
import { GuaranteeOfOriginCheckResult } from "./GuaranteeOfOriginCheckResult/GuaranteeOfOriginCheckResult";

export interface GuaranteeOfOriginGenerationViewProps {
  sites: Array<SiteBasic>;
}

function GuaranteeOfOriginGenerationView({
  sites
}: GuaranteeOfOriginGenerationViewProps) {
  const { showToast, showErrorToast } = useToast();
  const [startDate, setStartDate] = useState<DateTime | undefined>(undefined);
  const [endDate, setEndDate] = useState<DateTime | undefined>(undefined);
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
    undefined
  );

  const siteSelectionEnabled = !!startDate && !!endDate;
  const periods = asListOfPeriods(selectedSiteId, startDate, endDate);
  const {
    result,
    loading: isCheckLoading,
    error: checkError
  } = useGOCheck(periods);
  const { generateAndDownloadDocument, loading: isGenerationLoading } =
    useGOGenerate();
  const documentGenerationIsEnabled =
    result.length > 0 &&
    result.every((r) => r.isOnboardingComplete) &&
    !isCheckLoading;

  function handleDateRangeChanged(newDateRange: DateInputOnChangeValue) {
    const [newStart, newEnd] = newDateRange;
    setStartDate(newStart ?? undefined);
    setEndDate(newEnd ?? undefined);
  }

  function asListOfPeriods(
    siteId?: string,
    startDate?: DateTime,
    endDate?: DateTime
  ): Array<GuaranteeOfOriginPeriod> {
    if (!siteId || !startDate || !endDate) {
      return [];
    }

    return [
      {
        site: Number(siteId),
        startDate: luxonDateTimeToBackendDateOrDateTime(startDate),
        endDate: luxonDateTimeToBackendDateOrDateTime(endDate)
      }
    ];
  }

  function handleSiteChange(siteId: string | null) {
    if (siteId) {
      setSelectedSiteId(siteId);
    }
  }

  if (sites.length === 0) {
    return <NoDataMessage message="Keine Liegenschaften verfügbar" />;
  }

  function handleDocumentGeneration() {
    generateAndDownloadDocument(periods)
      .then(() => {
        showToast({
          type: "success",
          message:
            "Der Herkunftsnachweis für die ausgewählte Liegenschaft wurde erfolgreich erstellt."
        });
      })
      .catch((e) => {
        showErrorToast(e);
      });
  }

  const siteOptions = sites.map((site) => ({
    value: site.id.toString(),
    label: site.name
  }));

  return (
    <div className="GuaranteeOfOriginGenerationView">
      <Group gap="xl" justify="center" mb="xl">
        <DateRangeInput
          presets={{ type: DateRangePresets.YearsAndMonths }}
          onChange={handleDateRangeChanged}
        />
        <Select
          data={siteOptions}
          disabled={!siteSelectionEnabled}
          placeholder={
            siteSelectionEnabled
              ? "Wählen Sie eine Liegenschaft aus"
              : "Bitte zuerst einen Zeitraum wählen"
          }
          value={selectedSiteId}
          w={300}
          onChange={handleSiteChange}
        />
        <Button
          color="primary"
          disabled={!documentGenerationIsEnabled}
          loading={isGenerationLoading}
          onClick={handleDocumentGeneration}
        >
          Dokument erstellen
        </Button>
      </Group>
      {periods.length > 0 && (
        <LoadOrError
          error={checkError}
          loading={isCheckLoading}
          loadingMessage="Einen Moment bitte, wir prüfen die verfügbaren Daten."
        >
          <GuaranteeOfOriginCheckResult result={result} />
        </LoadOrError>
      )}
    </div>
  );
}

export { GuaranteeOfOriginGenerationView };
